import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useLocalization } from '@/hooks/useLocalization';

// Likelihood x impact matrix. Nothing in the data stores likelihood or impact
// directly, so both are derived from real signals and the rules are stated in
// the footnote:
//   impact     = worst open signal severity on the project
//   likelihood = how many open signals corroborate it
// Projects with no open signals are not placed on the grid.

type Rating = 'Low' | 'Medium' | 'High' | 'Extreme';

const IMPACT = ['Negligible', 'Minor', 'Moderate', 'Major', 'Catastrophic'] as const;
const LIKELIHOOD = ['Very Low', 'Low', 'Medium', 'High', 'Very High'] as const;

// Rows are likelihood (low to high), columns are impact (low to high).
const MATRIX: Rating[][] = [
  ['Low', 'Low', 'Low', 'Medium', 'High'],
  ['Low', 'Low', 'Medium', 'Medium', 'High'],
  ['Low', 'Medium', 'Medium', 'High', 'High'],
  ['Medium', 'Medium', 'High', 'High', 'Extreme'],
  ['Medium', 'Medium', 'High', 'Extreme', 'Extreme'],
];

const CELL: Record<Rating, string> = {
  Low: 'bg-lime-200 text-lime-950 dark:bg-lime-900/50 dark:text-lime-100',
  Medium: 'bg-yellow-200 text-yellow-950 dark:bg-yellow-900/50 dark:text-yellow-100',
  High: 'bg-orange-300 text-orange-950 dark:bg-orange-900/60 dark:text-orange-100',
  Extreme: 'bg-red-500 text-white dark:bg-red-700',
};

const IMPACT_BY_SEVERITY: Record<string, number> = { low: 1, medium: 2, high: 3, critical: 4 };

export interface HeatMapProject {
  id: string;
  name: string;
  openCount: number;
  maxSeverity: string | null;
}

interface Props {
  projects: HeatMapProject[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

const RiskHeatMap = ({ projects, selectedId, onSelect }: Props) => {
  const { tx, t } = useLocalization();
  const grid: HeatMapProject[][][] = LIKELIHOOD.map(() => IMPACT.map(() => []));
  let placed = 0;
  for (const p of projects) {
    if (!p.maxSeverity || p.openCount === 0) continue;
    const col = IMPACT_BY_SEVERITY[p.maxSeverity] ?? 0;
    const row = Math.min(p.openCount, 5) - 1;
    grid[row][col].push(p);
    placed++;
  }

  return (
    <Card className="nexus-fade-in">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">{t('Risk heat map')}</CardTitle>
        <p className="text-xs text-muted-foreground">
          {placed}  {t('of')} {projects.length}  {t('projects have open risks. Select a project to open it.')}
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] border-separate border-spacing-1 text-center text-xs" aria-label={t('Risk heat map by likelihood and impact')}>
            <thead>
              <tr>
                <th rowSpan={2} className="w-24 rounded-md bg-primary px-2 py-2 text-sm font-semibold text-primary-foreground">{t('Likelihood')}</th>
                <th colSpan={5} className="rounded-md bg-primary px-2 py-2 text-sm font-semibold text-primary-foreground">{t('Impact')}</th>
              </tr>
              <tr>
                {IMPACT.map((i) => (
                  <th key={i} className="rounded-md bg-primary/90 px-2 py-1.5 font-medium text-primary-foreground">{i}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...LIKELIHOOD].reverse().map((label, ri) => {
                const r = LIKELIHOOD.length - 1 - ri;
                return (
                  <tr key={label}>
                    <th scope="row" className="rounded-md bg-primary px-2 py-2 text-xs font-semibold text-primary-foreground">{tx(label)}</th>
                    {IMPACT.map((_, c) => {
                      const rating = MATRIX[r][c];
                      const here = grid[r][c];
                      return (
                        <td key={c} className={cn('h-14 rounded-md align-middle', CELL[rating])}>
                          {here.length === 0 ? (
                            <span className="text-[11px] font-medium opacity-60">{rating}</span>
                          ) : (
                            <div className="flex flex-col items-center gap-0.5 px-1">
                              <span className="text-[10px] font-medium opacity-70">{rating}</span>
                              <div className="flex flex-wrap justify-center gap-1">
                                {here.map((p) => (
                                  <button
                                    key={p.id}
                                    type="button"
                                    onClick={() => onSelect(p.id)}
                                    title={t('{0}: {1} open, worst {2}', { 0: p.name, 1: p.openCount, 2: p.maxSeverity })}
                                    aria-label={t('{0}, {1} risk. Open project.', { 0: p.name, 1: rating })}
                                    className={cn(
                                      'max-w-[110px] truncate rounded px-1.5 py-0.5 text-[11px] font-semibold bg-background/80 text-foreground shadow-sm hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                      selectedId === p.id && 'ring-2 ring-foreground',
                                    )}
                                  >
                                    {p.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-muted-foreground">
          
          {t('Impact is the worst open signal severity on the project (low is Minor, medium Moderate, high Major, critical Catastrophic). Likelihood is the number of open signals backing it (1 is Very Low up to 5 or more Very High).')}
        </p>
      </CardContent>
    </Card>
  );
};

export default RiskHeatMap;
